import { useState, useMemo, useCallback } from 'react'
import styles from '../style.module.scss'

const formatValue = (value, field, valueLabels) => {
  if (value === null || value === undefined || value === '') {
    return <span className={styles.emptyValue}>empty</span>
  }
  if (typeof value === 'object') {
    return JSON.stringify(value)
  }
  const str = String(value)
  if (valueLabels && field && valueLabels[field] && valueLabels[field][str]) {
    return (
      <>
        {str} <span className={styles.valueHint}>({valueLabels[field][str]})</span>
      </>
    )
  }
  return str
}

const formatFieldName = (field, labelMap) => {
  if (labelMap && labelMap[field]) {
    return labelMap[field]
  }
  return field
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
}

const formatTablePath = path =>
  path
    .split('.')
    .map(formatFieldName)
    .join(' \u2192 ')

// HasMany key fields used for record matching
const HASMANY_KEY_FIELDS = {
  disabilities: 'disability_type_id',
  language_proficiencies: 'language_id',
}

// Sub-tables that can be nested inside a parent table (e.g. inside "students")
const NESTED_SUB_TABLES = {
  student_details: 'flat',
  disabilities: 'hasMany',
  language_proficiencies: 'hasMany',
}

// --- Immutable path helpers ---
const getByPath = (obj, path) =>
  path.split('.').reduce((acc, key) => (acc && typeof acc === 'object' ? acc[key] : undefined), obj)

const setByPath = (obj, path, value) => {
  const parts = path.split('.')
  if (parts.length === 1) {
    return { ...(obj || {}), [parts[0]]: value }
  }
  const [head, ...rest] = parts
  const child =
    obj && typeof obj[head] === 'object' && !Array.isArray(obj[head]) ? obj[head] : {}
  return { ...(obj || {}), [head]: setByPath(child, rest.join('.'), value) }
}

/**
 * Normalize a value to an array of records for HasMany processing.
 * Handles: array → as-is, object → wrap in array, anything else → empty array.
 */
const normalizeToArray = value => {
  if (Array.isArray(value)) return value
  if (value && typeof value === 'object') return [value]
  return []
}

// --- Build HasMany pairs (matched by key field) ---
const buildHasManyPairs = (currentItems, newItems, keyField) => {
  const pairs = []
  const matchedNewIndices = new Set()

  currentItems.forEach(currentItem => {
    const keyValue = currentItem[keyField]
    const newIndex = newItems.findIndex(
      (ni, idx) => !matchedNewIndices.has(idx) && ni[keyField] === keyValue
    )
    if (newIndex >= 0) {
      matchedNewIndices.add(newIndex)
      pairs.push({ keyValue, currentItem, newItem: newItems[newIndex], type: 'matched' })
    } else {
      pairs.push({ keyValue, currentItem, newItem: null, type: 'current_only' })
    }
  })

  newItems.forEach((newItem, idx) => {
    if (!matchedNewIndices.has(idx)) {
      pairs.push({ keyValue: newItem[keyField], currentItem: null, newItem, type: 'new_only' })
    }
  })

  return pairs
}

export const CompareContactData = ({ item, onApprove, onReject, onClose }) => {
  const [selectedData, setSelectedData] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const afLabels = item.additional_field_labels || {}
  const valueLabels = item.value_labels || {}

  // Parse current_data and new_data into flat fields and HasMany tables
  const { flatFields, hasManyTables } = useMemo(() => {
    const flat = []
    const hasMany = []
    const currentData = item.current_data || {}
    const newData = item.new_data || {}

    const allTables = new Set([...Object.keys(currentData), ...Object.keys(newData)])

    allTables.forEach(table => {
      const currentTable = currentData[table]
      const newTable = newData[table]

      // Top-level HasMany (if any exist at top level)
      if (table in HASMANY_KEY_FIELDS) {
        const keyField = HASMANY_KEY_FIELDS[table]
        const pairs = buildHasManyPairs(
          normalizeToArray(currentTable),
          normalizeToArray(newTable),
          keyField
        )
        hasMany.push({ tablePath: table, keyField, pairs })
        return
      }

      // Object table — may contain nested sub-tables
      const currentObj =
        currentTable && typeof currentTable === 'object' && !Array.isArray(currentTable)
          ? currentTable
          : {}
      const newObj =
        newTable && typeof newTable === 'object' && !Array.isArray(newTable) ? newTable : {}
      const allFields = new Set([...Object.keys(currentObj), ...Object.keys(newObj)])

      allFields.forEach(field => {
        const currentValue = currentObj[field]
        const newValue = newObj[field]
        const subType = NESTED_SUB_TABLES[field]

        if (subType === 'hasMany' && field in HASMANY_KEY_FIELDS) {
          // Nested HasMany (e.g. students.disabilities)
          const keyField = HASMANY_KEY_FIELDS[field]
          const pairs = buildHasManyPairs(
            normalizeToArray(currentValue),
            normalizeToArray(newValue),
            keyField
          )
          hasMany.push({ tablePath: `${table}.${field}`, keyField, pairs })
        } else if (subType === 'flat') {
          // Nested flat object (e.g. students.student_details)
          const nestedCurObj =
            currentValue && typeof currentValue === 'object' && !Array.isArray(currentValue)
              ? currentValue
              : {}
          const nestedNewObj =
            newValue && typeof newValue === 'object' && !Array.isArray(newValue) ? newValue : {}
          const nestedFields = new Set([...Object.keys(nestedCurObj), ...Object.keys(nestedNewObj)])

          nestedFields.forEach(nestedField => {
            const nCurVal = nestedCurObj[nestedField]
            const nNewVal = nestedNewObj[nestedField]
            flat.push({
              tablePath: `${table}.${field}`,
              field: nestedField,
              currentValue: nCurVal,
              newValue: nNewVal,
              isChanged: JSON.stringify(nCurVal) !== JSON.stringify(nNewVal),
            })
          })
        } else {
          // Regular flat field
          flat.push({
            tablePath: table,
            field,
            currentValue,
            newValue,
            isChanged: JSON.stringify(currentValue) !== JSON.stringify(newValue),
          })
        }
      })
    })

    return { flatFields: flat, hasManyTables: hasMany }
  }, [item])

  // --- Flat field selection (with unselect) ---
  const handleSelectFlat = useCallback(
    (tablePath, field, source) => {
      setSelectedData(prev => {
        const container = getByPath(prev, tablePath) || {}
        const sourceData = source === 'current' ? item.current_data : item.new_data
        const sourceObj = getByPath(sourceData, tablePath) || {}
        const value = sourceObj[field]

        // If already selected with same source — unselect
        if (
          container[field] !== undefined &&
          JSON.stringify(container[field]) === JSON.stringify(value)
        ) {
          const { [field]: _, ...rest } = container
          return setByPath({ ...prev }, tablePath, rest)
        }

        // Select
        const updatedContainer = { ...container, [field]: value }
        return setByPath({ ...prev }, tablePath, updatedContainer)
      })
    },
    [item]
  )

  const isFlatFieldSelected = useCallback(
    (tablePath, field, source) => {
      const container = getByPath(selectedData, tablePath)
      if (!container || typeof container !== 'object' || Array.isArray(container)) return false
      if (container[field] === undefined) return false

      const selectedValue = container[field]
      const sourceData = source === 'current' ? item.current_data : item.new_data
      const sourceObj = getByPath(sourceData, tablePath) || {}
      return JSON.stringify(selectedValue) === JSON.stringify(sourceObj[field])
    },
    [selectedData, item]
  )

  // --- HasMany record selection (with unselect) ---
  const handleSelectHasManyRecord = useCallback((tablePath, keyValue, source, newItem) => {
    setSelectedData(prev => {
      const lastPart = tablePath.split('.').pop()
      const keyField = HASMANY_KEY_FIELDS[lastPart]

      let arr = getByPath(prev, tablePath)
      if (!Array.isArray(arr)) arr = []

      const alreadySelected = arr.some(r => r[keyField] === keyValue)

      // If clicking 'new' and already selected — unselect (toggle off)
      if (source === 'new' && alreadySelected) {
        arr = arr.filter(r => r[keyField] !== keyValue)
        return setByPath({ ...prev }, tablePath, arr)
      }

      // Remove existing selection for this key
      arr = arr.filter(r => r[keyField] !== keyValue)

      // If selecting new, add the new record
      if (source === 'new' && newItem) {
        arr = [...arr, newItem]
      }

      return setByPath({ ...prev }, tablePath, arr)
    })
  }, [])

  const isHasManyRecordSelected = useCallback(
    (tablePath, keyValue, source) => {
      const lastPart = tablePath.split('.').pop()
      const keyField = HASMANY_KEY_FIELDS[lastPart]
      const arr = getByPath(selectedData, tablePath)
      if (!Array.isArray(arr)) return false
      const hasEntry = arr.some(r => r[keyField] === keyValue)
      return source === 'new' ? hasEntry : false
    },
    [selectedData]
  )

  // Deep check if any values have been selected
  const hasSelections = useMemo(() => {
    const hasValues = obj => {
      if (obj === null || obj === undefined) return false
      if (Array.isArray(obj)) return obj.length > 0
      if (typeof obj === 'object') {
        return Object.keys(obj).length > 0 && Object.values(obj).some(v => hasValues(v))
      }
      return true
    }
    return hasValues(selectedData)
  }, [selectedData])

  const handleApprove = useCallback(async () => {
    if (!hasSelections) return
    setIsSubmitting(true)
    try {
      await onApprove(selectedData)
    } finally {
      setIsSubmitting(false)
    }
  }, [hasSelections, selectedData, onApprove])

  const handleReject = useCallback(async () => {
    const notes = prompt('Reason for rejection:')
    if (!notes) return
    setIsSubmitting(true)
    try {
      await onReject(notes)
    } finally {
      setIsSubmitting(false)
    }
  }, [onReject])

  const hasData = flatFields.length > 0 || hasManyTables.length > 0

  return (
    <div className={styles.reviewModal} onClick={onClose}>
      <div className={styles.reviewContent} onClick={e => e.stopPropagation()}>
        <div className={styles.reviewHeader}>
          <h3>Review Contact Update #{item.id}</h3>
          <button className={styles.closeButton} onClick={onClose}>
            &times;
          </button>
        </div>

        <div className={styles.reviewInfo}>
          <span>
            <strong>Contact:</strong> {item.contact_name || 'N/A'}
          </span>
          <span>
            <strong>Email:</strong> {item.contact_email || 'N/A'}
          </span>
          <span>
            <strong>Status:</strong>{' '}
            <span className={`${styles.statusBadge} ${styles[item.status]}`}>
              {item.status_label || item.status}
            </span>
          </span>
          {item.reviewed_by && (
            <span>
              <strong>Reviewed by:</strong> {item.reviewed_by}
            </span>
          )}
        </div>

        <div className={styles.reviewTable}>
          {!hasData ? (
            <div className={styles.noChanges}>No data to compare</div>
          ) : (
            <>
              {/* Flat fields table — only changed */}
              {flatFields.length > 0 && (
                <table>
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>Current Value</th>
                      <th>New Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {flatFields.map(({ tablePath, field, currentValue, newValue, isChanged }) => (
                      <tr key={`${tablePath}.${field}`}>
                        <td className={styles.fieldName}>
                          {formatFieldName(field, afLabels)}
                          <span className={styles.tableName}>{formatTablePath(tablePath)}</span>
                        </td>
                        <td
                          className={`${styles.valueCell} ${isFlatFieldSelected(tablePath, field, 'current') ? styles.selected : ''}`}
                          onClick={() => handleSelectFlat(tablePath, field, 'current')}
                          style={{ cursor: 'pointer' }}
                        >
                          {formatValue(currentValue, field, valueLabels)}
                        </td>
                        <td
                          className={`${styles.valueCell} ${isFlatFieldSelected(tablePath, field, 'new') ? styles.selected : ''} ${
                            isChanged ? styles.changed : ''
                          }`}
                          onClick={() => handleSelectFlat(tablePath, field, 'new')}
                          style={{ cursor: 'pointer' }}
                        >
                          {formatValue(newValue, field, valueLabels)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {hasManyTables.map(({ tablePath, keyField, pairs }) => (
                <div key={tablePath} className={styles.hasManySection}>
                  <h4 className={styles.hasManyTitle}>{formatTablePath(tablePath)}</h4>
                  <table>
                    <thead>
                      <tr>
                        <th>Record</th>
                        <th>Current</th>
                        <th>New</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pairs.map(({ keyValue, currentItem, newItem, type }) => {
                        const allFields = new Set([
                          ...Object.keys(currentItem || {}),
                          ...Object.keys(newItem || {}),
                        ])

                        return Array.from(allFields).map((field, fi) => {
                          const curVal = currentItem ? currentItem[field] : undefined
                          const newVal = newItem ? newItem[field] : undefined
                          const isChanged = JSON.stringify(curVal) !== JSON.stringify(newVal)

                          return (
                            <tr key={`${tablePath}.${keyValue}.${field}`}>
                              {fi === 0 && (
                                <td className={styles.fieldName} rowSpan={allFields.size}>
                                  {formatFieldName(keyField)}: {formatValue(keyValue, keyField, valueLabels) || 'N/A'}
                                  <span className={styles.tableName}>
                                    {type === 'new_only'
                                      ? '(new record)'
                                      : type === 'current_only'
                                        ? '(removed)'
                                        : ''}
                                  </span>
                                </td>
                              )}
                              <td
                                className={`${styles.valueCell} ${type === 'new_only' ? styles.emptyValue : ''}`}
                                onClick={
                                  currentItem
                                    ? () =>
                                        handleSelectHasManyRecord(
                                          tablePath,
                                          keyValue,
                                          'current',
                                          null
                                        )
                                    : undefined
                                }
                                style={{ cursor: currentItem ? 'pointer' : 'default' }}
                              >
                                {currentItem ? (
                                  <>
                                    <span className={styles.tableName}>
                                      {formatFieldName(field)}
                                    </span>
                                    {formatValue(curVal, field, valueLabels)}
                                  </>
                                ) : (
                                  fi === 0 && <span className={styles.emptyValue}>no record</span>
                                )}
                              </td>
                              <td
                                className={`${styles.valueCell} ${
                                  isHasManyRecordSelected(tablePath, keyValue, 'new')
                                    ? styles.selected
                                    : ''
                                } ${isChanged ? styles.changed : ''} ${type === 'current_only' ? styles.emptyValue : ''}`}
                                onClick={
                                  newItem
                                    ? () =>
                                        handleSelectHasManyRecord(
                                          tablePath,
                                          keyValue,
                                          'new',
                                          newItem
                                        )
                                    : undefined
                                }
                                style={
                                  newItem
                                    ? { cursor: 'pointer' }
                                    : { cursor: 'default' }
                                }
                              >
                                {newItem ? (
                                  <>
                                    <span className={styles.tableName}>
                                      {formatFieldName(field)}
                                    </span>
                                    {formatValue(newVal, field, valueLabels)}
                                  </>
                                ) : (
                                  fi === 0 && <span className={styles.emptyValue}>no record</span>
                                )}
                              </td>
                            </tr>
                          )
                        })
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
            </>
          )}
        </div>

        <div className={styles.reviewActions}>
          <button className={styles.btnCancel} onClick={onClose} disabled={isSubmitting}>
            Cancel
          </button>
          <button className={styles.btnReject} onClick={handleReject} disabled={isSubmitting}>
            Reject
          </button>
          <button
            className={styles.btnApprove}
            onClick={handleApprove}
            disabled={isSubmitting || !hasSelections}
          >
            {item.status === 'approved' ? 'Re-Approve' : 'Approve'}
          </button>
        </div>
      </div>
    </div>
  )
}
